/**
 * Journey step type definitions.
 *
 * Domain step types (IJourneyStep, IDelayStep, IActionStep, etc.) are imported
 * directly from @pushly/models — the single source of truth. No local aliases.
 *
 * This file contains only:
 *   1. View-model types (UI-layer extensions of js-models types)
 *   2. Type guard functions (accept IJourneyStep, narrow to specific members)
 *   3. React Flow integration types (JourneyNode, JourneyEdge, etc.)
 *   4. JourneyStepData class
 */
import { CreateDomainNotificationRequest } from '@pushly/models/lib/structs/notifications/create-requests/create-domain-notification-request'
import { Edge, Node } from '@xyflow/react'
import type { ElkEdgeSection } from 'elkjs/lib/elk-api'
import { UIEditTrackable } from '../../../structs/ui-edit-trackable'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import type { IRecommendedNotificationActionParams } from '@pushly/models/lib/structs/journeys'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { isMeaningfulStep } from '@pushly/models/lib/structs/journeys'
import { CopyTypes } from './journey-context'
import {
    CombinedItemStrategy,
    ItemRankingStrategy,
} from '@pushly/models/lib/enums/catalogs'
import { ITEM_STRATEGY_ACTION_TYPES } from '@pushly/models/lib/structs/journeys'
import type {
    IBaseStepConfiguration,
    ISendNotificationActionParams,
    IJourneyStep,
    IActionStep,
    IDelayStep,
    ITriggerStep,
    IGlobalTriggerStep,
    IExitStep,
    IConditionalStep,
    IVariantSplitStep,
    IPlaceholderStep,
} from '@pushly/models/lib/structs/journeys'

// region View-model types — UI-layer extensions of js-models types

/**
 * View-model type — UI-layer extension of ISendNotificationActionParams.
 * After the snake_case migration, NotificationPayload uses the same field names
 * as IJourneyActionNotificationRequest. However, NotificationPayload allows
 * null in some fields (e.g. title: string | null) where the interface requires
 * non-null, so we keep the Omit pattern for the notification field.
 */
export interface SendNotificationActionParams extends Omit<ISendNotificationActionParams, 'notification'> {
    notification?: CreateDomainNotificationRequest
}

/**
 * View-model type — UI-layer conditional configuration for BlueConic integration.
 * Maps SEND_RECOMMENDED_NOTIFICATION to BlueConicRecommendedNotificationParams.
 */
export type SendNotificationActionConfiguration<
    T extends JourneyActionType = JourneyActionType,
> = IBaseStepConfiguration<T> & {
    params: T extends JourneyActionType.SEND_RECOMMENDED_NOTIFICATION
        ? IRecommendedNotificationActionParams
        : SendNotificationActionParams
}

/**
 * View-model type — derived from SendNotificationActionConfiguration.
 * Narrows IActionStep with SendNotificationActionConfiguration<T>.
 */
export type SendActionStep<T extends JourneyActionType = JourneyActionType> =
    IActionStep & { configuration: SendNotificationActionConfiguration<T> }

export function isSendActionOfType<T extends JourneyActionType>(
    step: IActionStep,
    actionType: T,
): step is SendActionStep<T> {
    return step.configuration.type === actionType
}

export function isSendActionNode<T extends JourneyActionType>(
    node: JourneyNode,
    actionType: T,
): node is JourneyNode<SendActionStep<T>> {
    return isActionJourneyNode(node) && node.data.step.configuration.type === actionType
}

// endregion

// region Branching view-model types

export type BranchEdgeData = {
    branchKey: string
    percentage?: number
    elkSections?: ElkEdgeSection[]
}

export type ConditionOperator = 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than'

export function isConditionalStepJourneyNode(node: JourneyNode): node is JourneyNode<IConditionalStep> {
    return node.data?.step?.type === StepType.CONDITION
}

// endregion

// region Placeholder types

export type PlaceholderRole = 'empty' | 'convergence'

export function isPlaceholderJourneyNode(node: JourneyNode): node is JourneyNode<IPlaceholderStep> {
    return node.data?.step?.type === StepType.PLACEHOLDER
}

// endregion

// region Action step helpers

// Subset of action steps that support item_strategy
export function isSendWithItemStrategyActionStep(step: IActionStep): boolean {
    return ITEM_STRATEGY_ACTION_TYPES.has(step.configuration.type as JourneyActionType)
}

export function getSendParams(step: IActionStep): SendNotificationActionParams | undefined {
    return step.configuration.type !== JourneyActionType.SEND_RECOMMENDED_NOTIFICATION
        ? (step.configuration as SendNotificationActionConfiguration<JourneyActionType.SEND_NOTIFICATION>).params
        : undefined
}

export function getItemStrategy(step: IActionStep): CombinedItemStrategy | ItemRankingStrategy | undefined {
    return getSendParams(step)?.item_strategy
}

// endregion

// region Step-level type predicates
// Centralized in js-models — re-exported here for convenience.
export {
    isActionStep,
    isDelayStep,
    isTriggerStep,
    isGlobalTriggerStep,
    isExitStep,
    isVariantSplitStep,
    isConditionalStep,
    isPlaceholderStep,
} from '@pushly/models/lib/structs/journeys'

// endregion

// region JourneyNode / Edge Types

/** Shape of the optional `uiMeta.invalid` value attached to journey nodes. */
export type UiMetaInvalid = { reason?: string; options?: string[] }

/** ELK-computed port position for branching node source handles. */
export type ElkPortPosition = { portId: string; leftPercent: number }

/** Typed UI metadata attached to journey nodes. All fields are optional
 *  since different node types use different subsets. */
export type JourneyNodeUIMeta = {
    /** How the node was created: 'add' for new nodes, or a CopyTypes value for paste. */
    source?: 'add' | CopyTypes
    /** Validation error info displayed as a node toolbar. */
    invalid?: UiMetaInvalid
    /** Which branching node ID(s) this node is a join target of. */
    joinTargetOf?: string | string[]
    /** Which branching node ID this convergence placeholder belongs to. */
    convergenceOf?: string
    /** Role of a placeholder node: 'empty' (unfilled slot) or 'convergence' (join point). */
    placeholderRole?: PlaceholderRole
    /** ELK-computed port positions for branching node source handles. */
    elkPortPositions?: ElkPortPosition[]
    /**
     * Marks a synthetic per-variant performance card node. Present ONLY on
     * rendered-graph card nodes (never canonical). Identifies which split +
     * variant this card belongs to so the renderer can resolve metrics.
     */
    perfCardOf?: PerfCardMeta
}

/** Identity carried by a synthetic perf-card node (rendered-only). */
export type PerfCardMeta = {
    splitId: string
    branchKey: string
    variantName: string
    percentage?: number
}

export type JourneyNodeDataType<T> = { step: T; uiMeta: JourneyNodeUIMeta }
export type JourneyNode<T extends IJourneyStep = IJourneyStep> = Node<JourneyStepData<JourneyNodeDataType<T>>>

export function isGlobalTriggerJourneyNode(node: JourneyNode): node is JourneyNode<IGlobalTriggerStep> {
    return node.data.step.type === StepType.GLOBAL_TRIGGER
}

export function isTriggerJourneyNode(node: JourneyNode): node is JourneyNode<ITriggerStep> {
    return node.data.step.type === StepType.TRIGGER
}

export function isDelayJourneyNode(node: JourneyNode): node is JourneyNode<IDelayStep> {
    return node.data.step.type === StepType.DELAY
}

export function isActionJourneyNode(node: JourneyNode): node is JourneyNode<IActionStep> {
    return node.data.step.type === StepType.ACTION
}

export function isExitJourneyNode(node: JourneyNode): node is JourneyNode<IExitStep> {
    return node.data.step.type === StepType.EXIT
}

export function isVariantSplitJourneyNode(node: JourneyNode): node is JourneyNode<IVariantSplitStep> {
    return node.data?.step?.type === StepType.VARIANT_SPLIT
}

export type JourneyEdge = Edge<BranchEdgeData>

// endregion

// region UIEditTrackingClass
export class JourneyStepData<DataType extends JourneyNodeDataType<IJourneyStep>> extends UIEditTrackable<DataType> {
    readonly [x: string]: unknown

    constructor(data: DataType) {
        super(data, data.step.id)
    }

    public get step(): DataType['step'] {
        return this.getData().step
    }

    public get uiMeta(): DataType['uiMeta'] {
        return this.getData().uiMeta
    }
}

// endregion

// region Util Types

// Step types that produce linear (non-branching) nodes
export type LinearAddStepType =
    | StepType.DELAY
    | JourneyActionType.SEND_NOTIFICATION
    | JourneyActionType.SEND_CART_NOTIFICATION
    | JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION
    | JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION
    | JourneyActionType.SEND_RANKED_ITEM_NOTIFICATION
    | JourneyActionType.SEND_COMPLETED_ITEM_NOTIFICATION
    | JourneyActionType.SEND_RECOMMENDED_NOTIFICATION

// available types of nodes to add
export type AddStepType =
    | LinearAddStepType
    | StepType.VARIANT_SPLIT
    // TODO: Uncomment when CONDITION step is implemented
    // | StepType.CONDITION

export type EditableNode = JourneyNode<IActionStep | IDelayStep>

export type NodeEditorRef =
    | {
          onSubmit: () => NodeEditorSubmitResult | Promise<NodeEditorSubmitResult>
      }
    | undefined

export type NodeEditorSubmitResult = {
    nodeId: string
    step: IJourneyStep
    clearInvalid?: boolean
} | void

// endregion

// region Node predicates

/**
 * Returns true if the given node is a synthetic per-variant performance card
 * (rendered-graph only; never canonical).
 *
 * Gated on BOTH the node type marker (PERF_CARD_NODE_TYPE) and a structurally
 * valid perfCardOf payload. The type check is the reliable discriminator: it
 * avoids false positives from deep test mocks (vitest-mock-extended) that
 * auto-create truthy `uiMeta.perfCardOf` on otherwise-ordinary nodes.
 */
export function isPerfCardNode(node?: JourneyNode | null): boolean {
    // Literal kept in sync with PERF_CARD_NODE_TYPE in constants.ts. Inlined to
    // avoid a constants ↔ journey-nodes import cycle (constants imports types
    // from this module).
    if (!node || node.type !== 'perf-card') return false
    const meta = node.data?.uiMeta?.perfCardOf
    return !!meta && typeof meta.splitId === 'string' && typeof meta.branchKey === 'string'
}

/**
 * Returns true if the given node is an empty placeholder.
 */
export function isEmptyPlaceholder(node: JourneyNode): boolean {
    return node.data.step.type === StepType.PLACEHOLDER
        && node.data.uiMeta?.placeholderRole === 'empty'
}

/**
 * Returns true if the graph contains any meaningful (non-system) steps.
 */
export function graphHasMeaningfulSteps(nodes: JourneyNode[]): boolean {
    return nodes.some((n) => isMeaningfulStep(n.data.step.type))
}

// endregion

export type JourneyStatusRef = {
    builderHasChanged: boolean
    /** Reset dirty state after a successful save so navigation prompts don't fire. */
    markSaveComplete: () => void
}
